/**
 * Version topics for fine-grained React selectors (W1-09 state-store; ARCHITECTURE.md §5.3, §5.6).
 *
 * Topics: table names, singleton names, order lists, `line:<id>` (the line record, its tracks and keys) and
 * `notes:<lineId>` (the notes of a line). Every applied change bumps the counters of the topics it touches; `load`
 * advances a global epoch that every topic includes, so all selectors re-run after a document swap. Counters only
 * grow, so the sum of a selector's topic versions changes whenever one of its topics changed.
 *
 * Listeners subscribe to a set of topics and are called once per change that bumped at least one of them; a
 * listener on an EMPTY topic list (a selector that depends on no table) still runs after a document swap, because
 * its selector may read anything of the replaced document.
 */

import type { Unsubscribe } from '../core/util/types';
import type { ChartDoc, TableName } from '../core/model/types';
import type { DirtyScopes, Op } from '../core/ops/types';
import type { VersionTopic } from './types';
import { reportListenerError } from './emitter';

/** The topic of a line record with its tracks and keys. */
export function lineTopic(id: number): VersionTopic {
  return `line:${String(id)}` as VersionTopic;
}

/** The topic of the notes of a line. */
export function notesTopic(line: number): VersionTopic {
  return `notes:${String(line)}` as VersionTopic;
}

/** Topics touched by `ops` (applied to `before`, with `dirty` = Ops.dirtyOf(ops, before)). */
export function topicsOf(ops: readonly Op[], dirty: DirtyScopes, before: ChartDoc): Set<VersionTopic> {
  const out = new Set<VersionTopic>();
  const tables = new Set<TableName>();
  for (const op of ops) {
    switch (op.o) {
      case 'idSeq':
        break;
      case 'single':
        out.add(op.key);
        break;
      case 'order':
        out.add(op.list);
        break;
      case 'ins':
      case 'del':
      case 'set':
        tables.add(op.tbl);
        if (op.tbl === 'lines') {
          const id = op.o === 'set' ? op.id : op.rec.id;
          out.add(lineTopic(id));
          if (op.o !== 'set') out.add(notesTopic(id));
        } else if (op.tbl === 'notes') {
          if (op.o === 'set') {
            const cur = before.notes.get(op.id);
            if (cur !== undefined) out.add(notesTopic(cur.line));
            const nextLine = (op.next as { readonly line?: number }).line;
            if (typeof nextLine === 'number') out.add(notesTopic(nextLine));
          } else {
            out.add(notesTopic(op.rec.line));
          }
        }
        break;
    }
  }
  for (const t of tables) out.add(t);
  for (const id of dirty.lineProps) out.add(lineTopic(id));
  for (const id of dirty.lineMotion.keys()) out.add(lineTopic(id));
  for (const id of dirty.lineNotes) out.add(notesTopic(id));
  if (dirty.effects) {
    // Effect and video variable tracks live in tracks/keys; selectors on effects or videos must see them.
    out.add('effects');
    out.add('videos');
  }
  return out;
}

export class VersionCounters {
  private readonly counters = new Map<VersionTopic, number>();
  private readonly byTopic = new Map<VersionTopic, Set<() => void>>();
  private readonly all = new Set<() => void>();
  /** Listeners on an empty topic list: notified only when the document is replaced. */
  private readonly onSwap = new Set<() => void>();
  private epoch = 0;

  version(topic: VersionTopic): number {
    return this.epoch + (this.counters.get(topic) ?? 0);
  }

  /**
   * A number that changes whenever one of `topics` changed or the document was replaced (epoch plus the topics'
   * own counters; counters only grow, so the stamp only grows).
   */
  stamp(topics: readonly VersionTopic[]): number {
    let n = this.epoch;
    for (const t of topics) n += this.counters.get(t) ?? 0;
    return n;
  }

  /** Bumps `topics` and returns the listeners to notify (call `notify` after the change is fully applied). */
  bump(topics: Iterable<VersionTopic>): Set<() => void> {
    const due = new Set<() => void>();
    let any = false;
    for (const t of topics) {
      any = true;
      this.counters.set(t, (this.counters.get(t) ?? 0) + 1);
      const ls = this.byTopic.get(t);
      if (ls !== undefined) for (const cb of ls) due.add(cb);
    }
    if (any) for (const cb of this.all) due.add(cb);
    return due;
  }

  /** Advances every topic (document replaced) and returns every listener. */
  bumpAll(): Set<() => void> {
    this.epoch++;
    const due = new Set<() => void>(this.all);
    for (const ls of this.byTopic.values()) for (const cb of ls) due.add(cb);
    for (const cb of this.onSwap) due.add(cb);
    return due;
  }

  /**
   * Calls `cb` after every change that bumped one of `topics` (every change when `topics` is null; only document
   * swaps when `topics` is empty).
   */
  subscribe(topics: readonly VersionTopic[] | null, cb: () => void): Unsubscribe {
    // Wrap so the same function can subscribe twice independently.
    const fn = (): void => {
      cb();
    };
    // Own copy: the caller may reuse its array; unsubscribing must remove exactly what was added.
    const list = topics === null ? null : [...new Set(topics)];
    const set = list === null ? this.all : list.length === 0 ? this.onSwap : null;
    if (set !== null) set.add(fn);
    else if (list !== null) {
      for (const t of list) {
        let ls = this.byTopic.get(t);
        if (ls === undefined) {
          ls = new Set();
          this.byTopic.set(t, ls);
        }
        ls.add(fn);
      }
    }
    let active = true;
    return () => {
      if (!active) return;
      active = false;
      if (set !== null) {
        set.delete(fn);
        return;
      }
      for (const t of list ?? []) {
        const ls = this.byTopic.get(t);
        if (ls === undefined) continue;
        ls.delete(fn);
        if (ls.size === 0) this.byTopic.delete(t);
      }
    };
  }

  static notify(due: Iterable<() => void>): void {
    for (const cb of due) {
      try {
        cb();
      } catch (e) {
        reportListenerError(e);
      }
    }
  }
}
